"use client";

import {
	RiAppleFill,
	RiFacebookCircleFill,
	RiGlobalFill,
	RiInstagramFill,
	RiPlayListFill,
	RiShoppingBag3Fill,
	RiSoundcloudFill,
	RiSpotifyFill,
	RiTiktokFill,
	RiTwitterFill,
	RiYoutubeFill,
} from "@remixicon/react";
import { Button } from "@workspace/ui/components/button";
import { cn } from "@workspace/ui/lib/utils";
import Link from "next/link";
import { memo } from "react";

export interface SocialButtonGroupProps {
	name: string; // For accessibility (e.g., "Martin Garrix" or "Ultra Miami")
	links: {
		spotify?: string | null;
		youtube?: string | null;
		instagram?: string | null;
		twitter?: string | null;
		facebook?: string | null;
		tiktok?: string | null;
		soundcloud?: string | null;
		appleMusic?: string | null;
		website?: string | null;
		store?: string | null; // Festival-specific
		playlist?: string | null; // Festival-specific
	};
	size?: "xs" | "sm" | "md" | "lg";
	className?: string;
	maxVisible?: number; // Limit number of visible buttons
}

const sizeClasses = {
	xs: {
		gap: "gap-2",
		buttonSize: "iconSm" as const,
	},
	sm: {
		gap: "gap-1",
		buttonSize: "icon" as const,
	},
	md: {
		gap: "gap-1",
		buttonSize: "icon" as const,
	},
	lg: {
		gap: "gap-2",
		buttonSize: "icon" as const,
	},
};

const socialPlatforms = [
	{ key: "website", icon: RiGlobalFill, label: "Website" },
	{ key: "store", icon: RiShoppingBag3Fill, label: "Store" },
	{ key: "instagram", icon: RiInstagramFill, label: "Instagram" },
	{ key: "tiktok", icon: RiTiktokFill, label: "TikTok" },
	{ key: "youtube", icon: RiYoutubeFill, label: "YouTube" },
	{ key: "facebook", icon: RiFacebookCircleFill, label: "Facebook" },
	{ key: "twitter", icon: RiTwitterFill, label: "Twitter" },
	{ key: "spotify", icon: RiSpotifyFill, label: "Spotify" },
	{ key: "appleMusic", icon: RiAppleFill, label: "Apple Music" },
	{ key: "soundcloud", icon: RiSoundcloudFill, label: "SoundCloud" },
	{ key: "playlist", icon: RiPlayListFill, label: "Playlist" },
] as const;

export const SocialButtonGroup = memo<SocialButtonGroupProps>(
	function SocialButtonGroup({
		name,
		links,
		size = "md",
		className,
		maxVisible,
	}) {
		// Filter out platforms with no URL
		const availablePlatforms = socialPlatforms.filter(
			(platform) => links[platform.key as keyof typeof links],
		);

		if (availablePlatforms.length === 0) return null;

		// Apply maxVisible limit if specified
		const visiblePlatforms = maxVisible
			? availablePlatforms.slice(0, maxVisible)
			: availablePlatforms;

		const sizeConfig = sizeClasses[size];

		return (
			<div
				className={cn("flex flex-wrap items-center", sizeConfig.gap, className)}
			>
				{visiblePlatforms.map(({ key, icon: Icon, label }) => {
					const url = links[key as keyof typeof links];
					if (!url) return null;

					return (
						<Button key={key} asChild variant="secondary" size={sizeConfig.buttonSize}>
							<Link href={url} target="_blank" rel="noopener noreferrer">
								<Icon />
								<span className="sr-only">
									{name} on {label}
								</span>
							</Link>
						</Button>
					);
				})}
			</div>
		);
	},
);

SocialButtonGroup.displayName = "SocialButtonGroup";
